import { useEffect, useRef, useState } from 'react';

export const useLocalStorage = (
  key: string,
  defaultValue?: string
): ReturnType<typeof useState<string | undefined>> => {
  const rawValueRef = useRef<string | null>(null);
  const airshipKey = `airship:${key}`;

  const [value, setValue] = useState<string | undefined>(() => {
    rawValueRef.current = window.localStorage.getItem(airshipKey);
    return rawValueRef.current ? rawValueRef.current : defaultValue;
  });

  useEffect(() => {
    const updateLocalStorage = () => {
      if (value !== undefined) {
        const newValue = value;
        const oldValue = rawValueRef.current;
        rawValueRef.current = newValue;
        window.localStorage.setItem(airshipKey, newValue);
        window.dispatchEvent(
          new StorageEvent('storage', {
            storageArea: window.localStorage,
            url: window.location.href,
            key: airshipKey,
            newValue,
            oldValue,
          })
        );
      } else {
        window.localStorage.removeItem(airshipKey);
        window.dispatchEvent(
          new StorageEvent('storage', {
            storageArea: window.localStorage,
            url: window.location.href,
            key: airshipKey,
          })
        );
      }
    };

    updateLocalStorage();
  }, [airshipKey, value]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      const isValid =
        e.key === airshipKey &&
        e.storageArea === window.localStorage &&
        e.newValue !== rawValueRef.current;

      if (isValid) {
        rawValueRef.current = e.newValue;
        setValue(e.newValue ? e.newValue : undefined);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, [airshipKey]);

  return [value, setValue];
};
